/**
 * Your own side of the table: the name plate at the near edge of the felt, and
 * any sets you have exposed.
 *
 * Your concealed tiles are not here - they live on the rack below the felt,
 * where they are big enough to play with - and your discards are not here
 * either: they lie inside the wall with everybody else's, in the discard field.
 */

import type { PlayerId } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";

export interface SelfSeatProps {
  model: TableModel;
  nameFor: (playerId: PlayerId) => string;
}

export function SelfSeat({ model, nameFor }: SelfSeatProps) {
  const { t, seat: seatWind } = useI18n();
  const you = model.seats.find((seat) => seat.isYou);
  const ended = model.result !== null;
  const isTurn = !ended && model.turn === model.you && model.phase !== "awaiting-claims";
  const waiting = model.pendingClaims?.waitingOn.includes(model.you) ?? false;
  const discards = you?.discards ?? [];

  return (
    <section
      className={`seat seat--south seat--self ${isTurn ? "seat--turn" : ""} ${waiting ? "seat--waiting" : ""}`}
      data-seat={model.you}
      data-side="south"
      data-testid="seat-south"
    >
      <div className="seat__body">
        <header className="seat__plate seat__plate--self">
          <span className="seat__wind-chip" aria-hidden="true">
            {you ? seatWind(you.seat).slice(0, 1) : ""}
          </span>
          <span className="seat__name">
            {nameFor(model.you)}{" "}
            {you ? <span className="seat__wind">{seatWind(you.seat)}</span> : null}
          </span>
          <span className="seat__badges">
            {model.dealer === model.you ? (
              <span className="badge badge--dealer">{t("seat.dealerBadge")}</span>
            ) : null}
            {isTurn ? <span className="badge">{t("seat.turnBadge")}</span> : null}
            {waiting ? <span className="badge badge--claim">{t("seat.claimBadge")}</span> : null}
          </span>
          <span className="seat__discard-count">
            {t("hand.discards", { count: discards.length })}
          </span>
        </header>
      </div>
    </section>
  );
}
